import type { BeforeSendFn } from 'posthog-js';
import { appGuardDiagnostic } from './app-guard-diagnostic';

function text(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export const APP_GUARD_EVENT = 'workspace_app_guard_failure';

/**
 * Last before_send stage: rebuild only this event after SDK enrichment.
 * The SDK may already mark initial person properties sent before this strips
 * $set_once. That bounded first-event loss is documented and contract-tested;
 * do not mutate private SDK state to restore attribution.
 */
export const sanitizeAppGuardEvent: BeforeSendFn = (event) => {
  if (event.event !== APP_GUARD_EVENT) return event;
  const p = event.properties;
  const diagnostic = appGuardDiagnostic(
    p.error_code,
    {
      appNodeId: text(p.app_node_id),
      appVersion: text(p.app_version),
      source:
        p.bundle_source === 'draft' ||
        p.bundle_source === 'published' ||
        p.bundle_source === 'unpublished'
          ? p.bundle_source
          : undefined,
      buildSha: text(p.build_sha),
      manifestRevision: {
        nodeId: text(p.manifest_node_id) ?? '',
        contentGeneration:
          typeof p.manifest_content_generation === 'number'
            ? p.manifest_content_generation
            : -1,
        structuralGeneration:
          typeof p.manifest_structural_generation === 'number'
            ? p.manifest_structural_generation
            : -1,
      },
    },
    typeof p.error_operation === 'string' ? p.error_operation : '',
    typeof p.correlation_id === 'string' ? p.correlation_id : ''
  );
  if (!diagnostic?.correlation_id) return null;
  return {
    event: APP_GUARD_EVENT,
    uuid: event.uuid,
    timestamp: event.timestamp,
    properties: {
      ...diagnostic,
      // SDK overwrites this with its configured public ingestion key before hooks.
      // It is required for ingestion; it is not an application auth token.
      token: text(p.token),
      convex_request_id:
        typeof p.convex_request_id === 'string' &&
        /^[a-f0-9]{16,64}$/.test(p.convex_request_id)
          ? p.convex_request_id
          : undefined,
      distinct_id: `app-guard:${diagnostic.correlation_id}`,
      $is_identified: false,
      $process_person_profile: false,
    },
  };
};
